import { z } from "zod";

export const uuidSchema = z.string().uuid();

/** Every timestamp on the wire is an ISO 8601 string in UTC. */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * True when `yyyy-MM-dd` names a day that exists: a month from 01 to 12 and a day that the month
 * has (2019-02-29 does not exist, 2020-02-29 does). The shape alone let "2018-13-01" through to
 * the database.
 */
export function isRealIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Every calendar date on the wire is `yyyy-MM-dd` in the business timezone, and a real day. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected yyyy-MM-dd")
  .refine(isRealIsoDate, "Expected a real calendar date");
/** Wall clock time in the business timezone. */
export const localTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/, "Expected HH:mm");

export const idParamSchema = z.object({ id: uuidSchema });
export const codeParamSchema = z.object({ code: z.string().min(4).max(16) });

export const businessIdParamSchema = z.object({ businessId: uuidSchema });
export const businessChildParamSchema = z.object({ businessId: uuidSchema, id: uuidSchema });

export const dryRunQuerySchema = z.object({
  dryRun: z
    .union([z.literal("true"), z.literal("false"), z.boolean()])
    .optional()
    .transform((value) => value === true || value === "true"),
});

export const dateRangeQuerySchema = z.object({
  from: isoDateTimeSchema,
  to: isoDateTimeSchema,
});

export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

export const okSchema = z.object({ ok: z.literal(true) });

export const timestampsSchema = z.object({
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

/** AED amounts on the wire are always an integer count of fils, never a formatted string. */
export const filsSchema = z.number().int();

/** A no-show or late-cancel fee: AED 2 (the smallest card payment Stripe takes) to AED 10,000 (I2). */
export const feeFilsSchema = z.number().int().min(200).max(1_000_000);
export const bpsSchema = z.number().int().min(0).max(10_000);
